import Head from 'next/head';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import Link from 'next/link';
import useFeatureFlags from ':src/hooks/useFeatureFlags';
import { Question, QuestionWithDetails } from ':src/types';
import NewsletterSignup from ':src/modules/global/NewsletterSignup';
import apiFetch from ':src/utils/apiFetch';
import QuestionDetails from ':src/modules/questions/QuestionDetails';
import Incremental from ':src/modules/questions/Incremental';
import useMarkdown from ':src/utils/useMarkdown';
import parseMarkdownToDOM from ':src/utils/parseMarkdownToDOM';
import Tabs, { Tab, TabList, TabPanel } from ':src/components/Tabs';
import SolutionsTab from ':src/components/SolutionsTab';
import UserState from ':src/hooks/UserState';

export type QuestionPageProps = {
  question: QuestionWithDetails;
  firstImageSrc: string | null;
};

export const getStaticProps: GetStaticProps<
  QuestionPageProps,
  { slug: string }
> = async ({ params }) => {
  const { slug } = params!;
  try {
    const question = (await apiFetch<QuestionWithDetails>(
      `questions/${slug}`,
    ))!;

    const dom = parseMarkdownToDOM(question.prompt);
    const firstImageSrc = dom.querySelector('img')?.getAttribute('src') || null;

    return {
      props: {
        question,
        firstImageSrc,
      },
      revalidate: 3600,
    };
  } catch (err) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: (await apiFetch<Question[]>('questions'))!.map(({ slug }) => ({
      params: { slug },
    })),
    fallback: 'blocking',
  };
};

type QuestionPanelProps = {
  question: QuestionWithDetails;
};

function QuestionPanel({ question }: QuestionPanelProps) {
  const { prompt, hints, challenges, vanillaTemplateUrl, reactTemplateUrl } =
    question;

  const markdown = useMarkdown();

  return (
    <>
      <div className="mb-6 question__body">
        <h3 className="h5 mb-3">Prompt</h3>
        {/* eslint-disable-next-line  react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: markdown(prompt) }} />
      </div>
      <div className="mb-6">
        <p>To get started, fork one of the following Codepen templates:</p>
        <div className="mb-1">
          <Link href={reactTemplateUrl}>
            <a
              className="font-semibold hover:underline text-brand"
              target="_blank"
            >
              React template
            </a>
          </Link>
        </div>
        <div className="mb-1">
          <Link href={vanillaTemplateUrl}>
            <a
              className="font-semibold hover:underline text-brand"
              target="_blank"
            >
              Vanilla JavaScript template
            </a>
          </Link>
        </div>
      </div>
      <div className="mb-6">
        <h3 className="h5 mb-3">Hints</h3>
        <Incremental label="hint" items={hints} />
      </div>
      <h3 className="h5 mb-3">Additional challenges</h3>
      <Incremental label="challenge" items={challenges} />
    </>
  );
}

export default function QuestionPage({
  question,
  firstImageSrc,
}: QuestionPageProps) {
  const { title, description, slug } = question;
  const router = useRouter();
  const { isMVPLaunched } = useFeatureFlags();
  const { user } = UserState.useContainer();

  const pageURL = `${process.env.DOMAIN!}${router.asPath}`;

  return (
    <>
      <Head>
        <title>{title} | frontendeval</title>
        <meta name="description" content={description} />
        <link rel="icon" href="/favicon.ico" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageURL} />
        {firstImageSrc && (
          <meta
            property="og:image"
            content={`${process.env.DOMAIN!}${firstImageSrc}`}
          />
        )}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@frontendeval" />
        <link rel="canonical" href={pageURL} />
      </Head>
      <div className="container">
        <div className="content">
          <div className="question">
            <div className="mb-4">
              <h2 className="mb-4" id={title}>
                {title}
              </h2>
              <QuestionDetails question={question} />
            </div>
            {(!user || !isMVPLaunched) && <QuestionPanel question={question} />}
            {user && isMVPLaunched && (
              <Tabs initialValue="question">
                <div className="mb-6">
                  <TabList>
                    <Tab label="Question" value="question" />
                    <Tab label="Solutions" value="solutions" />
                  </TabList>
                </div>
                <TabPanel value="question">
                  <QuestionPanel question={question} />
                </TabPanel>
                <TabPanel value="solutions">
                  <SolutionsTab questionSlug={slug} />
                </TabPanel>
              </Tabs>
            )}
          </div>
        </div>
      </div>
      <NewsletterSignup />
    </>
  );
}
